import React, { useState, useEffect, useRef } from 'react';
import IconPicker from '../SprintDetail/shared/IconPicker';
import { renderIcon } from '../SprintDetail/FlowchartView/utils/renderIcon';
import { deepCopyTemplate, loadTemplateTreeForCopy } from '../../utils/templateCopy';

// Styles - import order: base → form → components → responsive
import './styles/base.css';
import './styles/form.css';
import './styles/custom-select.css';
import './styles/buttons.css';
import './styles/preview.css';
import './styles/responsive.css';

/**
 * Custom Dropdown Component
 * Fully styled dropdown to replace native <select>
 */
const CustomSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select...', 
  disabled = false,
  renderOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div 
      ref={dropdownRef}
      className={`modal__custom-select ${isOpen ? 'modal__custom-select--open' : ''} ${disabled ? 'modal__custom-select--disabled' : ''}`}
    >
      <button
        type="button"
        className="modal__custom-select-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className={`modal__custom-select-value ${!selectedOption ? 'modal__custom-select-value--placeholder' : ''}`}>
          {selectedOption ? (renderOption ? renderOption(selectedOption) : selectedOption.label) : placeholder}
        </span>
        <svg className="modal__custom-select-arrow" width="10" height="10" viewBox="0 0 10 10">
          <path fill="currentColor" d="M2 3L5 6L8 3"/>
        </svg>
      </button>
      
      {isOpen && (
        <div className="modal__custom-select-menu">
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              className={`modal__custom-select-option ${option.value === value ? 'modal__custom-select-option--selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {renderOption ? renderOption(option) : option.label}
              {option.value === value && (
                <svg className="modal__custom-select-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * CreateSprintModal Component
 * 
 * Modal for creating a new sprint - with two tabs:
 * 1. "New Campaign" - create a blank campaign
 * 2. "New From Template" - create from an existing template
 */
const CreateSprintModal = ({ 
  isOpen, 
  onClose, 
  onSprintCreated,
  preselectedTemplate = null,
  parentSprintId = null, // If provided, the new sprint will be nested under this parent
  templates = [], // Templates passed from parent (App.js)
  allSprints = [], // All sprints (for resolving nested campaigns in templates)
  loadFullSprint, // Loads full sprint/template data from API when only metadata is available
}) => {
  // Tab state: 'new' or 'template'
  const [activeTab, setActiveTab] = useState(preselectedTemplate ? 'template' : 'new');
  
  const [selectedTemplateId, setSelectedTemplateId] = useState(preselectedTemplate?.id || '');
  const [sprintName, setSprintName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Icon picker state
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerPosition, setIconPickerPosition] = useState(null);
  const iconButtonRef = useRef(null);

  // Update selected template when preselected changes
  useEffect(() => {
    if (preselectedTemplate) {
      setActiveTab('template');
      setSelectedTemplateId(preselectedTemplate.id);
      setSprintName(`${preselectedTemplate.name} - ${new Date().toLocaleDateString()}`);
      // Auto-populate icon from preselected template
      if (preselectedTemplate.icon) {
        setSelectedIcon(preselectedTemplate.icon);
      }
    }
  }, [preselectedTemplate]);

  // Auto-populate icon when template selection changes
  useEffect(() => {
    if (activeTab === 'template' && selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template?.icon) {
        setSelectedIcon(template.icon);
      }
    }
  }, [selectedTemplateId, templates, activeTab]);

  // Icon picker handlers
  const handleIconButtonClick = (e) => {
    e.stopPropagation();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuHeight = 440;
    const menuWidth = 380;
    
    if (iconButtonRef.current) {
      const rect = iconButtonRef.current.getBoundingClientRect();
      let top = rect.bottom + 4;
      if (top + menuHeight > viewportHeight) {
        top = rect.top - menuHeight - 4;
      }
      let left = rect.left;
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 10;
      }
      setIconPickerPosition({ x: left, y: top });
    }
    setShowIconPicker(true);
  };

  const handleIconChange = (newIcon) => {
    setSelectedIcon(newIcon);
    // Don't close picker - let user set both icon and color
  };

  const handleIconDeleted = () => {
    setSelectedIcon(null);
    setShowIconPicker(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    // Clear template selection when switching to "new" tab
    if (tab === 'new') {
      setSelectedTemplateId('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only name is required
    if (!sprintName.trim()) {
      setError('Please enter a campaign name');
      return;
    }

    // If on template tab, require a template selection
    if (activeTab === 'template' && !selectedTemplateId) {
      setError('Please select a template');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let newSprint;
      let nestedCampaignsToCreate = [];
      
      if (activeTab === 'template') {
        if (!loadFullSprint) {
          throw new Error('loadFullSprint is required to create campaigns from templates');
        }

        // Load full template + nested campaign data (list API is metadata-only)
        const { root: selectedTemplate, sprints: loadedSprints } = await loadTemplateTreeForCopy(
          selectedTemplateId,
          allSprints,
          loadFullSprint,
        );

        if (!selectedTemplate) {
          setError('Failed to load template data. Please try again.');
          return;
        }

        // Use deep-copy utility to copy template with all nested campaigns
        // This handles: new IDs, stripping date fields, remapping references
        const { rootCampaign, nestedCampaigns, nestedIdMap } = deepCopyTemplate(
          selectedTemplate, 
          loadedSprints,
          {
            newName: sprintName.trim(),
            newIcon: selectedIcon,
            parentSprintId: parentSprintId || null,
          }
        );
        
        newSprint = rootCampaign;
        nestedCampaignsToCreate = nestedCampaigns;
        
        console.log('[CreateSprintModal] Deep-copied template:', {
          templateName: selectedTemplate.name,
          taskCount: newSprint.tasks?.length || 0,
          nestedCampaignCount: nestedCampaignsToCreate.length,
          nestedIdMap,
        });
      } else if (activeTab === 'new') {
        // No template selected - create blank campaign
        newSprint = {
          name: sprintName.trim(),
          templateId: null,
          icon: selectedIcon || null,
          tasks: [],
          groups: [],
          // Do not set parentSprintId here. nestCampaign creates the mission
          // card first, then writes parentSprintId. Setting it early produces
          // sidebar-only nested campaigns when nesting fails or is delayed.
        };
      }
      
      // Pass nested campaigns to be created along with the root campaign
      onSprintCreated?.(newSprint, parentSprintId, nestedCampaignsToCreate);
    } catch (err) {
      setError('Failed to create campaign. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Don't close modal if icon picker is open
    if (showIconPicker) {
      setShowIconPicker(false);
      return;
    }
    setActiveTab(preselectedTemplate ? 'template' : 'new');
    setSelectedTemplateId(preselectedTemplate?.id || '');
    setSprintName('');
    setSelectedIcon(null);
    setShowIconPicker(false);
    setError(null);
    onClose();
  };

  // Handle overlay click - don't close if icon picker is open
  const handleOverlayClick = (e) => {
    // If icon picker is open, just close the icon picker, not the modal
    if (showIconPicker) {
      setShowIconPicker(false);
      return;
    }
    handleClose();
  };

  if (!isOpen) return null;

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // Convert templates to dropdown options
  // Use taskCount from metadata if available, otherwise fall back to tasks array length
  const templateOptions = templates.map(template => ({
    value: template.id,
    label: template.name,
    taskCount: template.taskCount ?? template.tasks?.length ?? 0,
  }));

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{parentSprintId ? 'Create Nested Campaign' : 'Create New Campaign'}</h2>
          <button className="modal__close-btn" onClick={handleClose}>×</button>
        </div>

        {/* Tab Switcher - Segmented Control */}
        <div className="modal__tabs">
          <div className="modal__tabs-container">
            <button
              type="button"
              className={`modal__tab ${activeTab === 'new' ? 'modal__tab--active' : ''}`}
              onClick={() => handleTabChange('new')}
            >
              Blank Campaign
            </button>
            <button
              type="button"
              className={`modal__tab ${activeTab === 'template' ? 'modal__tab--active' : ''}`}
              onClick={() => handleTabChange('template')}
              disabled={templates.length === 0}
              title={templates.length === 0 ? 'No templates available' : ''}
            >
              From Template
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            {error && (
              <div className="modal__error">{error}</div>
            )}

            {/* Template selector - only shown on template tab */}
            {activeTab === 'template' && (
              <div className="modal__field">
                <label className="modal__label">Template</label>
                <CustomSelect
                  value={selectedTemplateId}
                  onChange={setSelectedTemplateId}
                  options={templateOptions}
                  placeholder="Select a template..."
                  disabled={loading}
                  renderOption={(opt) => (
                    <span className="modal__template-option">
                      {opt.label}
                      <span className="modal__template-option-meta">{opt.taskCount} tasks</span>
                    </span>
                  )}
                />
                {selectedTemplate && (
                  <p className="modal__field-hint">
                    {selectedTemplate.description || 'No description'}
                  </p>
                )}
              </div>
            )}

            <div className="modal__field">
              <label className="modal__label">Enter Campaign Name:</label>
              <input
                type="text"
                className="modal__input"
                value={sprintName}
                onChange={(e) => setSprintName(e.target.value)}
                placeholder="e.g., Product Launch - March 2024"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="modal__field">
              <label className="modal__label">Choose an Icon <span className="modal__label-optional">(optional)</span>:</label>
              <button
                ref={iconButtonRef}
                type="button"
                className={`modal__icon-trigger ${selectedIcon ? 'modal__icon-trigger--has-icon' : ''}`}
                onClick={handleIconButtonClick}
                disabled={loading}
                title="Choose Icon"
              >
                {selectedIcon ? (
                  renderIcon(selectedIcon, { size: '24px' })
                ) : (
                  <span className="modal__icon-trigger-placeholder">+</span>
                )}
                <span className="modal__icon-trigger-label">{selectedIcon ? 'Change' : 'Add Icon'}</span>
              </button>
            </div>

            {/* Template preview - only shown when a template is selected */}
            {activeTab === 'template' && selectedTemplate && selectedTemplate.tasks?.length > 0 && (
              <div className="modal__preview">
                <label className="modal__label">Missions Preview</label>
                <ul className="modal__preview-list">
                  {selectedTemplate.tasks.slice(0, 5).map(task => (
                    <li key={task.id} className="modal__preview-item">
                      {task.name}
                      <span className="modal__preview-meta">
                        {task.subtasks?.length || 0} subtasks
                      </span>
                    </li>
                  ))}
                  {selectedTemplate.tasks.length > 5 && (
                    <li className="modal__preview-more">
                      +{selectedTemplate.tasks.length - 5} more missions
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="modal__footer">
            <button 
              type="button" 
              className="modal__btn modal__btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="modal__btn modal__btn--primary"
              disabled={loading || !sprintName.trim() || (activeTab === 'template' && !selectedTemplateId)}
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>

      {/* Icon Picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={selectedIcon}
          onIconChange={handleIconChange}
          onIconDeleted={handleIconDeleted}
          onClose={() => setShowIconPicker(false)}
          position={iconPickerPosition}
          nodeColor={null}
        />
      )}
    </div>
  );
};

export default CreateSprintModal;
